import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { apiCallBegan } from "./api";
import { getProblemSolvingSpentTime } from "./getters";
import { problemSubmissiondataUpdated } from "./items";
import { showToast } from "./message";

const slice = createSlice({
    name: 'work',
    initialState: {
        answers: {},
        correctionStatuses: {},
        drawings: {},
        timeSpent: {},
        currentProblemWorkingStartTime: null,
        currentProblem: null,
        processingSubmission: false,
    },
    reducers: {
        problemTextAnswerSet: (work, action) => {
            const problemId = action.payload.id;
            const value = action.payload.value;

            if (value === null || value === undefined) {
                work.answers[problemId] = '';
                return;
            }

            // Text answers should be stored as plain strings
            work.answers[problemId] = String(value);
        },
        problemAnswerChanged: (work, action) => {
            const problemId = action.payload.id;
            const selectedIndex = action.payload.value;
            const currentAnswer = work.answers[problemId];

            // Initialize as array if not already, or convert single value to array for backward compatibility
            let selectedAnswers = Array.isArray(currentAnswer)
                ? [...currentAnswer]
                : (currentAnswer !== null && currentAnswer !== undefined ? [currentAnswer] : []);

            // Toggle the selected index in the array
            const index = selectedAnswers.indexOf(selectedIndex);
            if (index > -1) {
                // Remove if already selected
                selectedAnswers.splice(index, 1);
            } else {
                // Add if not selected
                selectedAnswers.push(selectedIndex);
            }

            // Sort to maintain consistent order
            selectedAnswers.sort((a, b) => a - b);

            work.answers[problemId] = selectedAnswers;
        },
        problemAnswerSet: (work, action) => {
            const problemId = action.payload.id;
            let answerValue = action.payload.value;

            // Handle hydration from stored submissions
            if (Array.isArray(answerValue)) {
                // If it's already an array, use it directly
                work.answers[problemId] = [...answerValue].sort((a, b) => a - b);
            } else if (typeof answerValue === 'string') {
                // Try to parse JSON string
                try {
                    const parsed = JSON.parse(answerValue);
                    if (Array.isArray(parsed)) {
                        work.answers[problemId] = parsed.sort((a, b) => a - b);
                    } else {
                        // Single value, convert to array
                        work.answers[problemId] = [parseInt(parsed)];
                    }
                } catch (e) {
                    // Not JSON, treat as single value
                    work.answers[problemId] = [parseInt(answerValue)];
                }
            } else if (typeof answerValue === 'number') {
                // Single number, convert to array
                work.answers[problemId] = [answerValue];
            } else {
                // Unknown format, set empty array
                work.answers[problemId] = [];
            }
        },
        correctionStatusSelected: (work, action) => {
            work.correctionStatuses[action.payload.id] = action.payload.value;
        },
        submissionProcessStarted: (work, action) => {
            work.processingSubmission = true;
        },
        submissionProcessFinished: (work, action) => {
            work.processingSubmission = false;

            //clean current problem time spent data
            work.timeSpent[work.currentProblem] = undefined;
            work.currentProblemWorkingStartTime = moment().unix(); //on next submission problem working time will be calculated from now
        },
        workingOnProblemStarted: (work, action) => {
            const now = moment().unix();
            const newSelectedProblemId = action.payload;
            const timeSpent = work.timeSpent;
            /**
             * IF WE HAVE ALREADy a selected problem we try to record that problem  working time
             */
            if(work.currentProblem) {
                //if time spent is greater or equals 1 second we record that time for specified problem, other wise we skip
                if(now - work.currentProblemWorkingStartTime > 0) {
                    if(!timeSpent[work.currentProblem]) {
                        timeSpent[work.currentProblem] = 0;
                    }

                    /**
                     * add time spent to problem
                     */
                    timeSpent[work.currentProblem] =  timeSpent[work.currentProblem] + (now - work.currentProblemWorkingStartTime);
                    work.timeSpent = timeSpent;
                }
            }

            /**
             * change selected problem and fix its working start time
             */
            work.currentProblem = newSelectedProblemId;
            work.currentProblemWorkingStartTime = now;

        },
        resetWork: (work) => {
            work.answers = {};
            work.correctionStatuses = {};
            work.drawings = {};
            work.processingSubmission = false;
            work.timeSpent = {};
            work.currentProblemWorkingStartTime = null;
            work.currentProblem = null;
        }
    }
});

export default slice.reducer;
export const { workingOnProblemStarted, resetWork, correctionStatusSelected, problemAnswerChanged, problemAnswerSet, problemTextAnswerSet, submissionProcessStarted, submissionProcessFinished } = slice.actions;


// Action creators

/**
 * @param {*} id
 * @param {*} value
 * @param {*} isTeacher
 * @param {*} studentId if teacher review submission studentId should be passed
 */
export const submitProblem = (id, value, isTeacher = false, studentId = null) => (dispatch, getState) => {

    const url = isTeacher ? '/webapp/review-submission' : '/webapp/submit-work';

    // Handle array answers - convert to JSON string for backend compatibility
    // Backend will need to be updated to parse this, but for now we send as string
    let answerValue = value;
    if (Array.isArray(value)) {
        // Send as JSON string - backend can parse this later
        answerValue = JSON.stringify(value);
    }

    const data = { id: id, answer: answerValue };
    if(isTeacher) {
        data.studentId = studentId;
    } else {
        const { work } = getState()
        data['timeSpent'] = getProblemSolvingSpentTime(work, id);
    }


    return dispatch(apiCallBegan({
        url,
        method: 'post',
        data,
        onStart: submissionProcessStarted.type,
        onSuccess: [
            problemSubmissiondataUpdated.type, showToast.type, submissionProcessFinished.type
        ],
        onError: submissionProcessFinished.type,
        onFail: submissionProcessFinished.type
        // onStart: userLogInStart.type
    }));
}


